/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import { ArrayUtil } from '@cedalo/util';
import ImportIcon from '@mui/icons-material/esm/CloudDownload';
import ExportIcon from '@mui/icons-material/esm/CloudUpload';
import { Box, Checkbox, IconButton, MenuItem, Table, TableBody, TableCell, TableRow, TextField } from '@mui/material';
import formatDate from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import theme from '../../theme';
import StreamSheetPageTitle from '../../ui/utils/StreamSheetPageTitle';
import { withUser } from '../../UserProvider';
import { runQuery, useQuery } from '../app/GraphQLWSClient';
import EXPORT_QUERY from '../importexport/do-export.gql';
import TABLE_QUERY from '../importexport/export-table.gql';
import { ExportDialog } from '../importexport/ExportDialog';
import { ImportDropzone } from '../importexport/ImportDropzone';
import { StartImportDialog } from '../importexport/StartImportDialog';
import { StreamSheetAppBar } from '../StreamSheetAppBar';
import StreamSheetNavigation from '../StreamSheetNavigation';
import CustomTooltip from '../utils/CustomTooltip';
import { makeUseFilterAndSort } from '../utils/filterAndSort';
import StreamSheetBreadCrumbs from '../utils/StreamSheetBreadCrumbs';
import StreamSheetPaper from '../utils/StreamSheetPaper';
import TableSortHeader from '../utils/TableSortHeader';
import { AppAuth } from '../utils/Authorization';

const doExport = async (scope, machines, fileName, withConnections) => {
	try {
		const result = await runQuery(EXPORT_QUERY, { input: { scope, machines, withConnections } });
		if (result.export.success) {
			const blob = new Blob([JSON.stringify(result.export.data, null, 2)], {
				type: 'application/json;charset=utf8;'
			});
			saveAs(blob, fileName);
			return true;
		}
	} catch (error) {
		console.error('Export failed:', error);
	}
	return false;
};

const selectionArray = (selectionObject) =>
	Object.entries(selectionObject)
		.filter(([, v]) => v === true)
		.map(([k]) => k);

const selectionCount = (selectionObject) => selectionArray(selectionObject).length;

const toggleSelection = (selectionObject, key) => ({ ...selectionObject, [key]: !selectionObject[key] });

const defaultFileName = (apps, scopes, selectedApps, selectedScope) => {
	const timestamp = formatDate(new Date(), 'yyyy-MM-dd_HH-mm-ss');
	if (selectionCount(selectedApps) === 1) {
		const selectedApp = selectionArray(selectedApps)[0];
		const machine = apps.find((m) => m.id === selectedApp);
		const name = machine && machine.name;
		if (name) {
			return `${name}_${timestamp}`;
		}
	}
	const scope = scopes.find((s) => s.id === selectedScope);
	const name = scope && scope.name;
	if (name) {
		return `${name}_${timestamp}`;
	}
	return `streamsheets_export_${timestamp}`;
};

const drawerWidth = 60;

const useFilterAndSort = makeUseFilterAndSort(['name', 'description', 'lastModified'], 'name');

export const ExportPage = withUser(({ user }) => ({ scopes: user.scopes }))(({ scopes }) => {
	const [filter, setFilter] = useState('');
	const [search] = useSearchParams();
	const [currentScope, setCurrentScope] = useState(scopes[0].id);
	const [apps, setApps] = useState([]);
	const appsByScope = useMemo(() => ArrayUtil.partition(apps, (a) => a.scope.id), [apps]);
	const appsOfCurrentScope = appsByScope[currentScope];
	const tableQuery = useQuery(TABLE_QUERY);
	const [selectedApps, setSelectedApps] = useState({});
	const [showDialog, setShowDialog] = useState(false);
	const [showImportDialog, setShowImportDialog] = useState(false);
	// const [exportWithConnections, setExportWithConnections] = useState(true);

	const { values: filteredAndSortedApps, sort, setSortField } = useFilterAndSort(appsOfCurrentScope, filter);
	const hasSelection = Object.values(selectedApps).some((v) => v);

	const toggleApp = (appId) => setSelectedApps((current) => toggleSelection(current, appId));

	const handleScopeChange = (scope) => {
		setCurrentScope(scope);
		setSelectedApps({});
	};

	const toggleAllMachines = (event) => {
		const isSelected = event.target.checked;
		const selectionUpdate = filteredAndSortedApps.reduce((acc, cur) => ({ ...acc, [cur.id]: isSelected }), {});
		setSelectedApps((current) => ({ ...current, ...selectionUpdate }));
	};

	useEffect(() => {
		if (tableQuery.status === 'done') {
			setApps(
				tableQuery.result.machines.map((m) => ({
					...m,
					description: m.settings.description,
					lastModified: m.metadata.lastModified
				}))
			);
			const appParam = search.get('app');
			if (appParam) {
				const initialApp = tableQuery.result.machines.find((m) => m.id === appParam);
				if (initialApp) {
					setCurrentScope(initialApp.scope.id);
					toggleApp(appParam);
				}
			}
		} else if (tableQuery.status === 'error') {
			console.error('Failed to fetch apps!', ...tableQuery.result);
		}
	}, [tableQuery]);

	const onExportButton = async () => {
		if (selectionCount(selectedApps) === 0) {
			return;
		}
		setShowDialog(true);
	};
	const onConfirmExport = async (event, { fileName, withConnections }) => {
		setShowDialog(false);
		const appsToExport = selectionArray(selectedApps);
		const success = await doExport({ id: currentScope }, appsToExport, fileName, withConnections);
		if (!success) {
			console.error();
		}
	};
	const navigate = useNavigate();

	const hasConnections = selectedApps
		? (appsOfCurrentScope || []).some((app) => selectedApps[app.id] && app.connections.length)
		: false;
	const numberOfSelectedApps = selectedApps ? selectionCount(selectedApps) : 0;
	const numberOfApps = appsOfCurrentScope ? appsOfCurrentScope.length : 0;
	const canAddApp = AppAuth.canAdd(scopes, currentScope);
	return (
		<div style={{ width: 'inherit', height: 'inherit' }}>
			<div>
				<StreamSheetAppBar key="exportPageBar" helpContext="export" showFilter onFilterChange={setFilter} />
				<StreamSheetNavigation openPage={navigate} />
			</div>
			<Box
				sx={{
					position: 'relative',
					height: 'calc(100% - 50px)',
					width: `calc(100% - ${drawerWidth}px)`,
					marginLeft: `${drawerWidth}px`,
					overflow: 'hidden',
					bgcolor: theme.wall.backgroundColor
				}}
			>
				<div
					style={{
						display: 'inline-flex',
						width: 'calc(100% - 15px)',
						justifyContent: 'space-between'
					}}
				>
					<StreamSheetBreadCrumbs path="Home/Export" openPage={navigate} />

					<div
						style={{
							alignItems: 'end',
							display: 'flex',
							minHeight: '10px',
							marginTop: '5px'
						}}
					>
						<div
							style={{
								display: 'flex'
							}}
						>
							<CustomTooltip header="Tooltip.ImportApps" message="Tooltip.ImportAppsMessage">
								<IconButton onClick={() => setShowImportDialog(true)} size="small">
									<ImportIcon />
								</IconButton>
							</CustomTooltip>
							<CustomTooltip header="Tooltip.ExportApps" message="Tooltip.ExportAppsMessage">
								<div>
									<IconButton
										disabled={!hasSelection}
										daria-label="Add Workspace"
										onClick={onExportButton}
										size="small"
									>
										<ExportIcon />
									</IconButton>
								</div>
							</CustomTooltip>
							<StartImportDialog open={showImportDialog} onClose={() => setShowImportDialog(false)} />
						</div>
					</div>
				</div>
				<StreamSheetPaper>
					<ImportDropzone disabled={!canAddApp} scopeId={currentScope}>
						<Box
							sx={{
								height: 'calc(100%)',
								display: 'flex',
								flexDirection: 'column',
								width: 'calc(100% - 32px)'
							}}
						>
							<StreamSheetPageTitle
								title={<FormattedMessage id="Export.Title" defaultMessage="Export" />}
								subTitle={
									<FormattedMessage
										id="Export.TitleSub"
										defaultMessage="Select the apps from the list to export."
									/>
								}
								select={
									<TextField
										variant="outlined"
										size="small"
										margin="normal"
										select
										fullWidth
										value={currentScope}
										onChange={(event) => handleScopeChange(event.target.value)}
										label={<FormattedMessage id="Workspace.Title" defaultMessage="Workspaces" />}
									>
										{scopes.map(({ id, name }) => (
											<MenuItem value={id} key={id}>
												{name}
											</MenuItem>
										))}
									</TextField>
								}
							/>
							{/* <FormControlLabel
								sx={{ alignSelf: 'flex-end' }}
								control={
									<Checkbox
										checked={exportWithConnections}
										onChange={(event, state) => setExportWithConnections(state)}
									/>
								}
								label={
									<FormattedMessage
										id="Export.IncludeConnections"
										defaultMessage="Include the Connections in the export file"
									/>
								}
							/> */}
							<div style={{ height: 'calc(100% - 40px)', overflowY: 'auto' }}>
								<Table stickyHeader size="small">
									<TableSortHeader
										height={48}
										cells={[
											{
												id: 'checkbox',
												component: (
													<TableCell
														padding="checkbox"
														style={{
															width: '1%'
														}}
														sx={{
															maxWidth: '42px',
															backgroundColor: (t) =>
																t.palette.mode === 'dark' ? '#212121' : '#FFFFFF'
														}}
													>
														<Checkbox
															indeterminate={
																numberOfSelectedApps > 0 &&
																numberOfSelectedApps < numberOfApps
															}
															checked={
																numberOfSelectedApps > 0 &&
																numberOfSelectedApps >= numberOfApps
															}
															onChange={toggleAllMachines}
														/>
													</TableCell>
												)
											},
											{
												id: 'name',
												numeric: false,
												padding: false,
												label: 'ExportPage.Name',
												width: '25%'
											},
											{
												id: 'description',
												hideOnSmallDisplays: true,
												numeric: false,
												label: 'ExportPage.Description',
												width: '35%'
											},
											{
												id: 'lastModified',
												hideOnSmallDisplays: 'lg',
												numeric: false,
												label: 'ExportPage.LastModified',
												width: '17%'
											}
										]}
										orderBy={sort.field}
										order={sort.direction}
										onRequestSort={(event, property) => setSortField(property)}
									/>
									<TableBody>
										{filteredAndSortedApps.map((app) => {
											const isSelected = !!selectedApps[app.id];
											return (
												<TableRow
													style={{ height: '35px' }}
													onClick={() => toggleApp(app.id)}
													selected={isSelected}
													role="checkbox"
													aria-checked={isSelected}
													key={app.id}
												>
													<TableCell padding="checkbox">
														<Checkbox checked={isSelected} />
													</TableCell>
													<TableCell padding="none">{app.name}</TableCell>
													<TableCell
														sx={{
															display: {
																xs: 'none',
																sm: 'none',
																md: 'none',
																lg: 'table-cell',
																xl: 'table-cell'
															}
														}}
														padding="none"
													>
														{app.description}
													</TableCell>
													<TableCell
														sx={{
															display: {
																xs: 'none',
																sm: 'none',
																md: 'table-cell',
																lg: 'table-cell',
																xl: 'table-cell'
															}
														}}
														// onClick={() => handleSelection(resource)}
														padding="none"
													>
														{formatDistanceToNow(new Date(app.lastModified), {
															addSuffix: true
														})}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</div>
							{showDialog && (
								<ExportDialog
									open
									hasConnections={hasConnections}
									onCancel={() => setShowDialog(false)}
									onConfirm={onConfirmExport}
									fileName={defaultFileName(apps, scopes, selectedApps, currentScope)}
								/>
							)}
						</Box>
					</ImportDropzone>
				</StreamSheetPaper>
			</Box>
		</div>
	);
});
